import type {
  ServiceDefinition,
  Step,
  ServiceCategoryItem,
  PricingRow,
  InfoSection,
  FAQ,
} from "../types";

// ---------------------------------------------------------------------------
// Shared content across all bortforsling-mobler keyword variants
// ---------------------------------------------------------------------------

const steps: Step[] = [
  {
    number: 1,
    title: "Boka bortforsling av möbler",
    description:
      "Ange din adress, beskriv vilka möbler som ska bort och välj en tid som passar dig. Du ser priset direkt och kan boka online.",
  },
  {
    number: 2,
    title: "Vi hämtar dina möbler",
    description:
      "Våra ungdomar kommer till dig och bär ut alla möbler – oavsett om det handlar om en tung soffa, en garderob eller ett helt bohag. Vi hanterar trapphus, hissar och trånga utrymmen.",
  },
  {
    number: 3,
    title: "Möblerna tas om hand",
    description:
      "Möbler i gott skick skänks till välgörenhet. Resten körs till närmsta återvinningscentral där materialet sorteras och återvinns ansvarsfullt.",
  },
];

const categories: ServiceCategoryItem[] = [
  {
    image: "/sofa.webp",
    name: "Soffor",
    desc: "Soffgrupper, 2-sits, 3-sits och divansoffor",
  },
  {
    image: "/armchair.webp",
    name: "Stolar",
    desc: "Fåtöljer, läsfåtöljer, gungstolar och matstolar",
  },
  {
    image: "/bed.webp",
    name: "Sängar",
    desc: "Sängstommar, madrasser och sänggavlar",
  },
  {
    image: "/table.webp",
    name: "Bord",
    desc: "Matbord, köksbord, soffbord och sängbord",
  },
  {
    image: "/wardrobe.webp",
    name: "Förvaring",
    desc: "Byråer, garderober och förvaringsskap",
  },
  {
    image: "/patio-furniture.webp",
    name: "Utemöbler",
    desc: "Trädgårdsmöbler och utemöbler",
  },
];

const pricing: PricingRow[] = [
  {
    name: "Small",
    desc: "1–2 större möbler som soffa eller garderob",
    image: "/service-things-small.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 8 kr/km",
    avgCost: "995 kr",
  },
  {
    name: "Medium",
    desc: "3–5 möbler, t.ex. ett helt sovrum",
    image: "/service-room-medium.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 12 kr/km",
    avgCost: "1 495 kr",
  },
  {
    name: "Large",
    desc: "Alla möbler i ett rum eller en liten lägenhet",
    image: "/service-space-large.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 15 kr/km",
    avgCost: "2 495 kr",
  },
  {
    name: "X-Large",
    desc: "Komplett möbeltömning av bostad eller kontor",
    image: "/service-whole-place-x-large.webp",
    crew: "2 pers + skåpbil",
    framkorning: "20 km ingår, sedan 18 kr/km",
    avgCost: "4 995 kr",
  },
];

const infoSections: InfoSection[] = [
  {
    heading: "Bortforsling av möbler",
    content:
      "Har du en gammal soffa, en trasig garderob eller ett helt rum med möbler som behöver bort? Med Ungdomskraft slipper du allt krångel. Vi hämtar dina möbler direkt från hemmet, bär ner dem genom trapphus och hissar, och ser till att allt hanteras på rätt sätt – oavsett storlek och vikt.\n\nVåra unga, energiska medarbetare har lång erfarenhet av att flytta och forsla bort möbler av alla slag. Du behöver bara visa vad som ska bort – vi sköter resten.",
  },
  {
    heading: "Varför boka bortforsling av möbler med Ungdomskraft?",
    listItems: [
      {
        label: "Enkel bokning utan krångel -",
        text: "Boka din möbelbortforsling på några minuter. Fyll i adress, välj datum och få pris direkt – slipp väntan på offert. Behöver du hjälp redan idag? Vi kan vara på plats redan inom 60 minuter. Du kan även ringa oss på 08-517 082 81.",
      },
      {
        label: "Vi bär ut allt åt dig -",
        text: "Du behöver inte lyfta ett finger. Våra ungdomar bär ut möblerna från vilken våning och genom vilka utrymmen som helst – trånga trapphus, smala hissar och innergårdar är inga problem.",
      },
      {
        label: "Möbler skänks eller återvinns -",
        text: "Möbler i gott skick skänks vidare till organisationer som Myrorna och Stadsmissionen. Resten sorteras och lämnas på godkänd återvinningsstation. Du kan även ha rätt till RUT-avdrag när möbler transporteras till återanvändning.",
      },
      {
        label: "Försäkrat och tryggt -",
        text: "Alla uppdrag är försäkrade genom Trygg-Hansa. Din bostad och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
    ],
  },
  {
    heading: "Vilka möbler kan vi forsla bort?",
    content:
      "Vi hämtar och forslar bort alla typer av möbler, oavsett storlek och skick:",
    listItems: [
      { label: "Soffor & fåtöljer -", text: "soffgrupper, bäddsoffor, divaner, recliners och fåtöljer" },
      { label: "Sängar -", text: "sängstommar, madrasser, resårbottnar, sänggavlar och våningssängar" },
      { label: "Bord & stolar -", text: "matbord, soffbord, skrivbord, stolar och barstolar" },
      { label: "Förvaring -", text: "bokhyllor, garderober, byråer, kommoder, skåp och hyllsystem" },
      { label: "Kontorsmöbler -", text: "skrivbord, kontorsstolar, arkivskåp, konferensbord och receptionsdiskar" },
      { label: "Övriga möbler -", text: "TV-möbler, skoställ, speglar, hatthyllor och trädgårdsmöbler" },
    ],
    afterContent:
      "Vi monterar även isär möbler som behöver demonteras innan de kan bäras ut. Är du osäker på om vi kan hantera just dina möbler? Hör av dig så hjälper vi dig.",
  },
  {
    heading: "Så fungerar bortforsling av möbler i tre enkla steg",
    listItems: [
      {
        label: "1. Beskriv dina möbler -",
        text: "Fyll i vårt bokningsformulär med adress, vilka möbler som ska bort och önskat datum. Du får ett pris direkt – inga dolda kostnader.",
      },
      {
        label: "2. Vi hämtar möblerna -",
        text: "Våra ungdomar dyker upp på överenskommen tid, monterar isär det som behövs, bär ut allt och lastar på bilen. Du behöver inte göra något.",
      },
      {
        label: "3. Ansvarsfull hantering -",
        text: "Möbler i bra skick skänks vidare till välgörenhet. Resten sorteras och lämnas på godkänd återvinningsstation. Allt hanteras så att så lite som möjligt hamnar på deponi.",
      },
    ],
  },
  {
    heading: "Erfarna möbelbärare med lokalkännedom",
    content:
      "Våra ungdomar har gedigen erfarenhet av att bära och forsla bort möbler. De vet hur man manövrerar en stor soffa genom ett trångt trapphus, hur man skyddar väggar och golv vid tunga lyft, och hur man lastar effektivt för att minimera antalet turer.\n\nVi utför dagligen möbelbortforsling av allt från enstaka fåtöljer till kompletta bostadstömningar. Den erfarenheten gör att vi kan ge dig ett exakt pris redan vid bokning och genomföra uppdraget smidigt och utan fördröjningar.",
  },
  {
    heading: "Mer än bara bortforsling av möbler – du gör skillnad",
    content:
      "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar bortforsling av möbler hos oss får du inte bara professionell hjälp – du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid.\n\nVarje uppdrag bemannas av motiverade ungdomar som får lära sig allt från kundkontakt och logistik till fysiskt arbete och ansvarsfullt miljöarbete. Det är möbelbortforsling som gör skillnad.",
  },
  {
    heading: "Boka bortforsling av möbler idag",
    content:
      'Vill du bli av med gamla möbler snabbt och smidigt? Med Ungdomskraft får du professionell möbelbortforsling till fast pris. Vi hämtar direkt från hemmet, bär ut allt och ser till att möblerna hamnar rätt – antingen hos välgörenhet eller på återvinningsstationen.\n\nFyll i formuläret så får du pris direkt. Inga offertförfrågningar – bara enkel bortforsling av möbler som dessutom gör skillnad. Du kan även ringa oss på 08-517 082 81.',
    cta: { text: "Boka bortforsling av möbler", href: "#priser" },
  },
];

const faqs: FAQ[] = [
  {
    q: "Vilka typer av möbler kan ni forsla bort?",
    a: "Vi hämtar och forslar bort alla typer av möbler – soffor, sängar, bord, stolar, garderober, bokhyllor, byråer, TV-möbler, kontorsmöbler och mycket mer. Det spelar ingen roll om möblerna är tunga, stora eller svåra att komma åt. Våra ungdomar är vana vid att hantera alla sorters möbler i alla slags utrymmen.",
  },
  {
    q: "Kan ni bära ut möbler från vilken våning som helst?",
    a: "Ja, våra ungdomar hämtar möbler oavsett var de står – på tredje våningen utan hiss, i en källare eller på en vind. Vi är vana vid trånga trapphus, smala hissar och svåråtkomliga utrymmen. Du behöver inte bära ut något själv.",
  },
  {
    q: "Monterar ni isär möbler innan bortforsling?",
    a: "Ja, vi hjälper gärna till med isärmontering av möbler som behöver demonteras innan de kan bäras ut. Våra ungdomar har med sig standardverktyg. Om möblerna kräver specialverktyg eller monteringsinstruktioner ber vi dig ha dessa redo. Tid för isärmontering ingår i din totala arbetstid.",
  },
  {
    q: "Vad händer med möblerna efter bortforslingen?",
    a: "Möbler i gott skick skänks vidare till organisationer som Myrorna eller Stadsmissionen. Trasiga eller slitna möbler lämnas på godkänd återvinningsstation där materialet – trä, metall, textil – sorteras och återvinns. Vårt mål är att så lite som möjligt hamnar på deponi.",
  },
  {
    q: "Kan ni skänka mina möbler till välgörenhet?",
    a: "Ja! Om dina möbler fortfarande är i bra skick kan vi leverera dem till en secondhand-butik eller välgörenhetsorganisation istället för att köra dem till tippen. Ange bara önskad inlämningsplats vid bokning. Transport till återanvändning kan dessutom ge dig rätt till RUT-avdrag (25 %).",
  },
  {
    q: "Hur ska jag förbereda inför bortforsling av möbler?",
    a: "Du behöver egentligen inte göra mycket – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen kan du tömma lådor och skåp i förväg och rensa en fri väg (ca 1 meter bred) från entrén till möblerna. Det minskar arbetstiden och håller nere kostnaden.",
  },
  {
    q: "Erbjuder ni bortforsling av möbler samma dag?",
    a: "Ja, vi erbjuder både samdags- och nästadagsservice. Du kan boka våra ungdomar med så kort varsel som 30 minuter eller planera upp till 30 dagar i förväg. Välj den tid som passar dig bäst i bokningsformuläret.",
  },
  {
    q: "Vad kostar bortforsling av möbler?",
    a: "Priset beror på antalet möbler, deras storlek och hur lång tid uppdraget tar. Du får ett pris direkt i vår bokning baserat på vad du anger – inga dolda avgifter. Avfallsavgifter på återvinningsstationen tillkommer separat och ligger oftast runt 400 kr. Kontakta oss för en skräddarsydd offert vid större uppdrag.",
  },
  {
    q: "Kan jag få RUT-avdrag på bortforsling av möbler?",
    a: "Om dina möbler transporteras till en secondhand-butik, välgörenhetsorganisation eller annan plats för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där möblerna körs direkt till återvinningsstation ger inte rätt till RUT-avdrag. Avdraget hanteras direkt på fakturan.",
  },
  {
    q: "Ingår avfallsavgifter i priset?",
    a: "Nej, avfalls- och deponiavgifter ingår inte i priset utan debiteras separat. Avgiften varierar beroende på återvinningsstation samt vikten och typen av möbler, men ligger oftast runt 400 kr. Du ser alltid den totala kostnaden innan vi påbörjar uppdraget.",
  },
  {
    q: "Hur lång tid tar det att forsla bort möbler?",
    a: "Det varierar beroende på antal möbler och hur tillgängliga de är. En enkel bortforsling av en soffa och ett par stolar tar vanligtvis 30–45 minuter. Tömning av möblerna i en hel lägenhet kan ta en halv till en hel dag. Våra ungdomar jobbar effektivt och är vana vid tunga lyft.",
  },
  {
    q: "Kan ni hjälpa med möbeltömning av dödsbo?",
    a: "Ja, vi har gedigen erfarenhet av att tömma dödsbon och hela lägenheter på möbler. Våra ungdomar hanterar allt från soffor och sängar till garderober och skåp. Vi sorterar noggrant – möbler i bra skick skänks vidare och resten lämnas på godkänd återvinningsstation.",
  },
  {
    q: "Vilka områden erbjuder ni bortforsling av möbler i?",
    a: "Vi erbjuder bortforsling av möbler i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
  },
];

// ---------------------------------------------------------------------------
// Keyword variants
// ---------------------------------------------------------------------------

export const bortforslingMobler: ServiceDefinition = {
  id: "bortforsling-mobler",
  name: "Bortforsling möbler",
  keywords: [
    {
      slug: "bortforsling-mobler",
      keyword: "Bortforsling möbler",
      headingPrefix: "Hjälp med",
      headingKeyword: "bortforsling av möbler {{kommun}}",
      subtext:
        "Boka bortforsling av möbler {{kommun}} – soffor, sängar, garderober och alla typer av möbler. Vi skickar ungdomar som bär ut och forslar bort allt, ofta samma dag. Fast pris, enkel bokning och över 50 000 slutförda uppdrag.",
      heroImage: "/hero-bortforsling.webp",
      featureCardsHeading:
        "Vi sköter bortforsling av möbler {{kommun}}",
      howItWorksHeading: "Boka bortforsling av möbler {{kommun}} i 3 enkla steg",
      howItWorksSteps: steps,
      serviceCategoriesHeading: "Möbler vi forslar bort {{kommun}}",
      serviceCategoriesItems: categories,
      valuePropHeading: "Bortforsling av möbler {{kommun}} prislista",
      valuePropRows: pricing,
      priceEstimateSubtitle:
        "Ange dina adresser för att se priser och boka bortforsling av möbler {{kommun}}",
      availableAreasHeading:
        "Få hjälp med bortforsling av möbler i dessa kommuner",
      serviceInfoHeading: "Bortforsling av möbler {{kommun}}",
      serviceInfoSections: infoSections,
      serviceFAQHeading: "Frågor och svar - Bortforsling av möbler {{kommun}}",
      serviceFAQItems: faqs,
      metaTitle:
        "Bortforsling av möbler {{kommun}} – Snabb & Smidig | Ungdomskraft",
      metaDescription:
        "Boka bortforsling av möbler {{kommun}} – soffor, sängar, garderober och mer. Fast pris, enkel bokning. Ungdomskraft – över 50 000 slutförda uppdrag.",
    },
  ],
};
